import { getFunctions, httpsCallable } from 'firebase/functions';

const functions = getFunctions();

// Auto-approval callable functions
export const getTodayAIApprovals = async (data: { taskId: string }): Promise<{ count: number; date: string }> => {
  const callable = httpsCallable(functions, 'getTodayAIApprovals');
  
  try {
    const result = await callable(data);
    return result.data as { count: number; date: string };
  } catch (error) {
    console.error('Error calling getTodayAIApprovals:', error);
    throw error;
  }
};

export const updateTaskAutoRules = async (data: { taskId: string; autoRules: any }): Promise<{ success: boolean }> => {
  const callable = httpsCallable(functions, 'updateTaskAutoRules');
  
  try {
    const result = await callable(data);
    return result.data as { success: boolean };
  } catch (error) {
    console.error('Error calling updateTaskAutoRules:', error);
    throw error;
  }
};

// Dataset snapshot callable functions
export const createSnapshotForTask = async (data: {
  taskId: string;
  name?: string;
  split?: { train: number; val: number; test: number };
  negativesFromOtherFood?: boolean;
  maxPerClass?: number;
  minResolution?: { w: number; h: number };
}): Promise<{
  snapshotId: string;
  uri: string;
  counts: { train: number; val: number; test: number; positives: number; negatives: number };
  classes: string[];
}> => {
  const callable = httpsCallable(functions, 'createSnapshotForTask');
  
  try {
    const result = await callable(data);
    return result.data as {
      snapshotId: string;
      uri: string;
      counts: { train: number; val: number; test: number; positives: number; negatives: number };
      classes: string[];
    };
  } catch (error) {
    console.error('Error calling createSnapshotForTask:', error);
    throw error;
  }
};

// Model training callable functions
export const startTraining = async (data: {
  snapshotIds: string[];
  modelName?: string;
  base?: 'yolov8n-cls.pt' | 'yolov8s-cls.pt';
  epochs?: number;
  imgSize?: number;
  lr?: number;
}): Promise<{ modelId: string }> => {
  const callable = httpsCallable(functions, 'startTraining');
  
  try {
    const result = await callable(data);
    return result.data as { modelId: string };
  } catch (error) {
    console.error('Error calling startTraining:', error);
    throw error;
  }
};

export const getTrainingStatus = async (data: { modelId: string }): Promise<{
  status: 'queued' | 'running' | 'succeeded' | 'failed';
  metrics?: any;
  artifactUri?: string;
}> => {
  const callable = httpsCallable(functions, 'getTrainingStatus');
  
  try {
    const result = await callable(data);
    return result.data as {
      status: 'queued' | 'running' | 'succeeded' | 'failed';
      metrics?: any;
      artifactUri?: string;
    };
  } catch (error) {
    console.error('Error calling getTrainingStatus:', error);
    throw error;
  }
};

export const promoteModel = async (data: { modelId: string }): Promise<{
  ok: boolean;
  modelId: string;
}> => {
  const callable = httpsCallable(functions, 'promoteModel');
  
  try {
    const result = await callable(data);
    return result.data as { ok: boolean; modelId: string };
  } catch (error) {
    console.error('Error calling promoteModel:', error);
    throw error;
  }
};

// QA metrics callable functions
export const computeQaMetrics = async (data: { taskId: string; windowDays?: number }): Promise<{
  success: boolean;
  metrics: any;
  readiness: any;
}> => {
  const callable = httpsCallable(functions, 'computeQaMetrics');
  
  try {
    const result = await callable(data);
    return result.data as {
      success: boolean;
      metrics: any;
      readiness: any;
    };
  } catch (error) {
    console.error('Error calling computeQaMetrics:', error);
    throw error;
  }
};

// Active learning callable functions
export const runActiveLearning = async (data: { taskId: string }): Promise<{
  success: boolean;
  selected: number;
  enqueued: number;
  candidates: Array<{
    submissionId: string;
    aiScore: number;
    age: number;
    reason: string;
  }>;
}> => {
  const callable = httpsCallable(functions, 'runActiveLearning');
  
  try {
    const result = await callable(data);
    return result.data as {
      success: boolean;
      selected: number;
      enqueued: number;
      candidates: Array<{
        submissionId: string;
        aiScore: number;
        age: number;
        reason: string;
      }>;
    };
  } catch (error) {
    console.error('Error calling runActiveLearning:', error);
    throw error;
  }
};

export const previewActiveLearning = async (data: { taskId: string; limit?: number }): Promise<{
  candidates: Array<{
    submissionId: string;
    aiScore: number;
    age: number;
    reason: string;
    mediaUrl?: string;
  }>;
  totalAvailable: number;
}> => {
  const callable = httpsCallable(functions, 'previewActiveLearning');
  
  try {
    const result = await callable(data);
    return result.data as {
      candidates: Array<{
        submissionId: string;
        aiScore: number;
        age: number;
        reason: string;
        mediaUrl?: string;
      }>;
      totalAvailable: number;
    };
  } catch (error) {
    console.error('Error calling previewActiveLearning:', error);
    throw error;
  }
};

// Callable function to re-run a labeling job
export const reRunLabelingJob = async (jobId: string): Promise<{ success: boolean; jobId: string }> => {
  const callable = httpsCallable(functions, 'reRunLabelingJob');
  
  try {
    const result = await callable({ jobId });
    return result.data as { success: boolean; jobId: string };
  } catch (error) {
    console.error('Error calling reRunLabelingJob:', error);
    throw error;
  }
};

// Callable function to assign a job to current user
export const assignJob = async (jobId: string): Promise<{ success: boolean }> => {
  const callable = httpsCallable(functions, 'assignJob');
  
  try {
    const result = await callable({ jobId });
    return result.data as { success: boolean };
  } catch (error) {
    console.error('Error calling assignJob:', error);
    throw error;
  }
};

// Callable function to submit job for QC
export const submitForQC = async (jobId: string, labels: any[]): Promise<{ success: boolean }> => {
  const callable = httpsCallable(functions, 'submitForQC');
  
  try {
    const result = await callable({ jobId, labels });
    return result.data as { success: boolean };
  } catch (error) {
    console.error('Error calling submitForQC:', error);
    throw error;
  }
};

// Callable function for QC decisions
export const qcDecision = async (jobId: string, action: 'approve' | 'needs_fix' | 'reject', reasons: string[]): Promise<{ success: boolean }> => {
  const callable = httpsCallable(functions, 'qcDecision');
  
  try {
    const result = await callable({ jobId, action, reasons });
    return result.data as { success: boolean };
  } catch (error) {
    console.error('Error calling qcDecision:', error);
    throw error;
  }
};

// Callable function to trigger dataset export
export const exportDataset = async (versionTag?: string): Promise<{ success: boolean; exported: number }> => {
  const callable = httpsCallable(functions, 'exportDataset');
  
  try {
    const result = await callable({ versionTag });
    return result.data as { success: boolean; exported: number };
  } catch (error) {
    console.error('Error calling exportDataset:', error);
    throw error;
  }
};

// Callable function to claim an annotation
export const claimAnnotation = async (jobId: string): Promise<{ success: boolean; claimed?: boolean; alreadyLocked?: boolean }> => {
  const callable = httpsCallable(functions, 'claimAnnotation');
  
  try {
    const result = await callable({ jobId });
    return result.data as { success: boolean; claimed?: boolean; alreadyLocked?: boolean };
  } catch (error) {
    console.error('Error calling claimAnnotation:', error);
    throw error;
  }
};

// Callable function to release an annotation
export const releaseAnnotation = async (jobId: string): Promise<{ success: boolean }> => {
  const callable = httpsCallable(functions, 'releaseAnnotation');
  
  try {
    const result = await callable({ jobId });
    return result.data as { success: boolean };
  } catch (error) {
    console.error('Error calling releaseAnnotation:', error);
    throw error;
  }
};

// Review callables
export const approveAnnotation = async (jobId: string, diff?: any): Promise<{ success: boolean }> => {
  const callable = httpsCallable(functions, 'approveAnnotation');
  
  try {
    const result = await callable({ jobId, diff });
    return result.data as { success: boolean };
  } catch (error) {
    console.error('Error calling approveAnnotation:', error);
    throw error;
  }
};

export const requestChanges = async (jobId: string, notes?: string, reasons?: string[]): Promise<{ success: boolean }> => {
  const callable = httpsCallable(functions, 'requestChanges');
  
  try {
    const result = await callable({ jobId, notes, reasons });
    return result.data as { success: boolean };
  } catch (error) {
    console.error('Error calling requestChanges:', error);
    throw error;
  }
};

export const rejectAnnotation = async (jobId: string, notes: string, reasons: string[]): Promise<{ success: boolean }> => {
  const callable = httpsCallable(functions, 'rejectAnnotation');
  
  try {
    const result = await callable({ jobId, notes, reasons });
    return result.data as { success: boolean };
  } catch (error) {
    console.error('Error calling rejectAnnotation:', error);
    throw error;
  }
};

// Dataset callables
export const startDatasetBuild = async (datasetId: string): Promise<{ success: boolean; buildId: string; version: number }> => {
  const callable = httpsCallable(functions, 'startDatasetBuild');
  
  try {
    const result = await callable({ datasetId });
    return result.data as { success: boolean; buildId: string; version: number };
  } catch (error) {
    console.error('Error calling startDatasetBuild:', error);
    throw error;
  }
};

export const getDatasetDownloadUrls = async (datasetId: string): Promise<{
  manifestJson?: string;
  cocoJson?: string;
  yoloZip?: string;
  csvFile?: string;
  archiveZip?: string;
}> => {
  const callable = httpsCallable(functions, 'getDatasetDownloadUrls');
  
  try {
    const result = await callable({ datasetId });
    return result.data as any;
  } catch (error) {
    console.error('Error calling getDatasetDownloadUrls:', error);
    throw error;
  }
};

// Model Operations callables
export const registerModel = async (data: { model: any }): Promise<{ success: boolean; modelId: string }> => {
  const callable = httpsCallable(functions, 'registerModel');
  
  try {
    const result = await callable(data);
    return result.data as { success: boolean; modelId: string };
  } catch (error) {
    console.error('Error calling registerModel:', error);
    throw error;
  }
};

export const startCanary = async (data: { deploySpec: any }): Promise<{ success: boolean; deployId: string }> => {
  const callable = httpsCallable(functions, 'startCanary');
  
  try {
    const result = await callable(data);
    return result.data as { success: boolean; deployId: string };
  } catch (error) {
    console.error('Error calling startCanary:', error);
    throw error;
  }
};

export const adjustTraffic = async (deployId: string, traffic: { baseline: number; canary: number }): Promise<{ success: boolean }> => {
  const callable = httpsCallable(functions, 'adjustTraffic');
  
  try {
    const result = await callable({ deployId, traffic });
    return result.data as { success: boolean };
  } catch (error) {
    console.error('Error calling adjustTraffic:', error);
    throw error;
  }
};

export const endCanary = async (deployId: string, action: 'promote' | 'rollback' | 'abort'): Promise<{ success: boolean }> => {
  const callable = httpsCallable(functions, 'endCanary');
  
  try {
    const result = await callable({ deployId, action });
    return result.data as { success: boolean };
  } catch (error) {
    console.error('Error calling endCanary:', error);
    throw error;
  }
};

// Sales callables
export const createOrUpdateProduct = async (data: { product: any }): Promise<{ success: boolean; productId: string }> => {
  const callable = httpsCallable(functions, 'createOrUpdateProduct');
  
  try {
    const result = await callable(data);
    return result.data as { success: boolean; productId: string };
  } catch (error) {
    console.error('Error calling createOrUpdateProduct:', error);
    throw error;
  }
};

export const createCheckoutSession = async (data: { productId: string; buyerEmail: string }): Promise<{ success: boolean; sessionUrl: string; sessionId: string }> => {
  const callable = httpsCallable(functions, 'createCheckoutSession');
  
  try {
    const result = await callable(data);
    return result.data as { success: boolean; sessionUrl: string; sessionId: string };
  } catch (error) {
    console.error('Error calling createCheckoutSession:', error);
    throw error;
  }
};

export const getEntitledDownloads = async (data: { entitlementId: string; buyerEmail: string }): Promise<{ success: boolean; downloadUrls: any }> => {
  const callable = httpsCallable(functions, 'getEntitledDownloads');
  
  try {
    const result = await callable(data);
    return result.data as { success: boolean; downloadUrls: any };
  } catch (error) {
    console.error('Error calling getEntitledDownloads:', error);
    throw error;
  }
};

export const sendMagicDownloadLink = async (data: { entitlementId: string }): Promise<{ success: boolean; downloadUrl: string; expiresAt: number; buyerEmail: string }> => {
  const callable = httpsCallable(functions, 'sendMagicDownloadLink');
  
  try {
    const result = await callable(data);
    return result.data as { success: boolean; downloadUrl: string; expiresAt: number; buyerEmail: string };
  } catch (error) {
    console.error('Error calling sendMagicDownloadLink:', error);
    throw error;
  }
};

export const suspendSales = async (): Promise<{ success: boolean; message: string }> => {
  const callable = httpsCallable(functions, 'suspendSales');
  
  try {
    const result = await callable({});
    return result.data as { success: boolean; message: string };
  } catch (error) {
    console.error('Error calling suspendSales:', error);
    throw error;
  }
};

// Model Assist callables
export const preannotateJob = async (jobId: string): Promise<{ success: boolean; suggestionsCount: number; message: string }> => {
  const callable = httpsCallable(functions, 'preannotateJob');
  
  try {
    const result = await callable({ jobId });
    return result.data as { success: boolean; suggestionsCount: number; message: string };
  } catch (error) {
    console.error('Error calling preannotateJob:', error);
    throw error;
  }
};

export const acceptSuggestion = async (jobId: string, suggestionId: string): Promise<{ success: boolean; message: string }> => {
  const callable = httpsCallable(functions, 'acceptSuggestion');
  
  try {
    const result = await callable({ jobId, suggestionId });
    return result.data as { success: boolean; message: string };
  } catch (error) {
    console.error('Error calling acceptSuggestion:', error);
    throw error;
  }
};

export const rejectSuggestion = async (jobId: string, suggestionId: string): Promise<{ success: boolean; message: string }> => {
  const callable = httpsCallable(functions, 'rejectSuggestion');
  
  try {
    const result = await callable({ jobId, suggestionId });
    return result.data as { success: boolean; message: string };
  } catch (error) {
    console.error('Error calling rejectSuggestion:', error);
    throw error;
  }
};

// Workforce management functions
export const requestNextJob = async (filters?: { 
  allowedTaskIds?: string[]; 
  minPriority?: number; 
  requireSkills?: string[] 
}): Promise<{ ok: boolean; jobId?: string; reason?: string }> => {
  const callable = httpsCallable(functions, 'requestNextJob');
  
  try {
    const result = await callable(filters || {});
    return result.data as { ok: boolean; jobId?: string; reason?: string };
  } catch (error) {
    console.error('Error calling requestNextJob:', error);
    throw error;
  }
};

export const releaseJob = async (jobId: string, reason?: string): Promise<{ ok: boolean }> => {
  const callable = httpsCallable(functions, 'releaseJob');
  
  try {
    const result = await callable({ jobId, reason });
    return result.data as { ok: boolean };
  } catch (error) {
    console.error('Error calling releaseJob:', error);
    throw error;
  }
};

// Training & Model Management
export const startTrain = async (datasetId: string, versionTag: string, taskId: string, recipe?: any): Promise<{ ok: boolean; requestId: string }> => {
  const callable = httpsCallable(functions, 'startTrain');
  
  try {
    const result = await callable({ datasetId, versionTag, taskId, recipe });
    return result.data as { ok: boolean; requestId: string };
  } catch (error) {
    console.error('Error calling startTrain:', error);
    throw error;
  }
};

export const completeTrain = async (requestId: string, artifactUrl: string): Promise<{ ok: boolean; modelId: string }> => {
  const callable = httpsCallable(functions, 'completeTrain');
  
  try {
    const result = await callable({ requestId, artifactUrl });
    return result.data as { ok: boolean; modelId: string };
  } catch (error) {
    console.error('Error calling completeTrain:', error);
    throw error;
  }
};

export const evaluateModel = async (modelId: string, datasetId: string, versionTag: string): Promise<{ ok: boolean; metrics: any }> => {
  const callable = httpsCallable(functions, 'evaluateModel');
  
  try {
    const result = await callable({ modelId, datasetId, versionTag });
    return result.data as { ok: boolean; metrics: any };
  } catch (error) {
    console.error('Error calling evaluateModel:', error);
    throw error;
  }
};

export const deleteSnapshot = async (data: { snapshotId: string }): Promise<{
  deleted: boolean;
  resetCount: number;
}> => {
  const callable = httpsCallable(functions, 'deleteSnapshot');

  try {
    const result = await callable(data);
    return result.data as { deleted: boolean; resetCount: number };
  } catch (error) {
    console.error('Error calling deleteSnapshot:', error);
    throw error;
  }
};

export const deleteModel = async (data: { modelId: string }): Promise<{
  deleted: boolean;
}> => {
  const callable = httpsCallable(functions, 'deleteModel');
  try {
    const result = await callable(data);
    return result.data as { deleted: boolean };
  } catch (error) {
    console.error('Error calling deleteModel:', error);
    throw error;
  }
};

// Test snapshot function
export const testSnapshot = async (): Promise<{
  success: boolean;
  tasksCount: number;
  completedSubmissionsCount: number;
  message: string;
}> => {
  const callable = httpsCallable(functions, 'testSnapshot');
  
  try {
    const result = await callable({});
    return result.data as {
      success: boolean;
      tasksCount: number;
      completedSubmissionsCount: number;
      message: string;
    };
  } catch (error) {
    console.error('Error calling testSnapshot:', error);
    throw error;
  }
};

// User Portal Functions
export const exportUserData = async (): Promise<{
  exportId: string;
  recordCount: number;
}> => {
  const callable = httpsCallable(functions, 'exportUserData');
  
  try {
    const result = await callable({});
    return result.data as { exportId: string; recordCount: number };
  } catch (error) {
    console.error('Error calling exportUserData:', error);
    throw error;
  }
};

export const getUserStats = async (): Promise<{
  totalSubmissions: number;
  approvedSubmissions: number;
  rejectedSubmissions: number;
  pendingSubmissions: number;
  totalRewardEarned: number;
}> => {
  const callable = httpsCallable(functions, 'getUserStats');
  
  try {
    const result = await callable({});
    return result.data as {
      totalSubmissions: number;
      approvedSubmissions: number;
      rejectedSubmissions: number;
      pendingSubmissions: number;
      totalRewardEarned: number;
    };
  } catch (error) {
    console.error('Error calling getUserStats:', error);
    throw error;
  }
};

export const deleteUserAccount = async (): Promise<{
  success: boolean;
}> => {
  const callable = httpsCallable(functions, 'deleteUserAccount');
  
  try {
    const result = await callable({});
    return result.data as { success: boolean };
  } catch (error) {
    console.error('Error calling deleteUserAccount:', error);
    throw error;
  }
};

